"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
  type ReactNode,
} from "react";
import Image from "next/image";
import { shapeAsset } from "@/lib/shapes";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { useMotion } from "@/contexts/MotionContext";
import { SITE_TAGLINE } from "@/lib/constants/app.constants";

// Same three assets on both title bars; each shape matches service accent (yellow / pink / teal).
const SHAPE_NAV = [
  {
    href: "/connected",
    label: "Connected & Intelligent",
    src: shapeAsset("pyramid", "yellow"),
  },
  {
    href: "/growth",
    label: "Digital Growth",
    src: shapeAsset("ball", "pink"),
  },
  {
    href: "/medical",
    label: "Medical & Regulated",
    src: shapeAsset("cube", "teal"),
  },
] as const;

// Shared chrome: shape row + title — same size on every window.
const WINDOW_HEADER_TITLE_CLASS =
  "font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted-light dark:text-text-muted-dark sm:text-[11px]";

const TYPEWRITER_TERMS = [
  "digital systems,",
  "marketing sites,",
  "medical software,",
  "connected devices,",
] as const;

/** xl+ only: drag is awkward on touch; tablet uses a static two-column layout instead. */
const XL_UP_MQ = "(min-width: 1280px)";

function useIsXlUp() {
  const [matches, setMatches] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia(XL_UP_MQ);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return matches;
}

function HeadlineTypewriter({ enabled }: { enabled: boolean }) {
  const [termIndex, setTermIndex] = useState(0);
  const [typed, setTyped] = useState<string>(TYPEWRITER_TERMS[0]);
  const [deleting, setDeleting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setCursorVisible(true);
      return;
    }
    const id = window.setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);
    return () => window.clearInterval(id);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setTermIndex(0);
      setTyped(TYPEWRITER_TERMS[0]);
      setDeleting(false);
      return;
    }

    const current = TYPEWRITER_TERMS[termIndex];
    const doneTyping = typed === current;
    const empty = typed.length === 0;

    let delay = deleting ? 44 : 78;
    if (!deleting && doneTyping) delay = 1280;
    if (deleting && empty) delay = 220;

    const id = window.setTimeout(() => {
      if (!deleting && doneTyping) {
        setDeleting(true);
        return;
      }
      if (deleting && empty) {
        setDeleting(false);
        setTermIndex((prev) => (prev + 1) % TYPEWRITER_TERMS.length);
        return;
      }

      if (deleting) {
        setTyped((prev) => prev.slice(0, -1));
      } else {
        setTyped(current.slice(0, typed.length + 1));
      }
    }, delay);

    return () => window.clearTimeout(id);
  }, [deleting, enabled, termIndex, typed]);

  return (
    <span className="inline-block min-w-[13ch] sm:min-w-[15ch]">
      <span>{typed}</span>
      <span aria-hidden className="ml-0.5 opacity-70">
        {cursorVisible ? "_" : " "}
      </span>
    </span>
  );
}

function WindowShapeButtons() {
  return (
    <div className="flex shrink-0 items-center gap-1.5 pr-1">
      {SHAPE_NAV.map(({ href, label, src }) => (
        <Link
          key={href}
          href={href}
          className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-black/[0.06] transition hover:scale-105 hover:ring-black/10 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-1 dark:ring-white/10 dark:hover:ring-white/18"
          aria-label={label}
        >
          <Image
            src={src}
            alt=""
            width={15}
            height={15}
            className="pointer-events-none h-[15px] w-[15px] object-contain drop-shadow-[0_0.5px_1px_rgba(0,0,0,0.14)] dark:drop-shadow-[0_0.5px_1.5px_rgba(0,0,0,0.4)]"
          />
        </Link>
      ))}
    </div>
  );
}

interface DraggableWindowProps {
  title: string;
  constrainRef: RefObject<HTMLDivElement | null>;
  stackZClass: string;
  className?: string;
  panelMaxClassName?: string;
  titleClassName?: string;
  /** Glass hero cards vs. dark terminal-styled panel. */
  surface?: "glass" | "terminal";
  showShapeButtons?: boolean;
  /** Overrides default body padding when set (e.g. compact grids). */
  bodyPadClassName?: string;
  /** Overrides default thick shell ring on glass windows (mobile/tablet). */
  shellPadClassName?: string;
  shellToneClassName?: string;
  backdropAccentClassName?: string;
  children: ReactNode;
  dragEnabled: boolean;
  /** When set, the title bar text links here (e.g. /llms.txt). Pointer events stop drag so the link is clickable. */
  titleHref?: string;
}

function DraggableWindow({
  title,
  constrainRef,
  stackZClass,
  className = "",
  panelMaxClassName = "max-w-[min(94vw,36rem)]",
  titleClassName = "min-w-0 shrink whitespace-normal sm:whitespace-nowrap",
  surface = "glass",
  showShapeButtons = true,
  bodyPadClassName,
  shellPadClassName,
  shellToneClassName,
  backdropAccentClassName,
  children,
  dragEnabled,
  titleHref,
}: DraggableWindowProps) {
  const isTerminal = surface === "terminal";

  const windowHeaderSurface = isTerminal
    ? "border-b border-white/[0.09] bg-[#141414]"
    : "border-b border-current/15 bg-[rgba(254,249,236,0.72)] dark:bg-[rgba(43,40,40,0.62)]";

  const windowBodySurface = isTerminal
    ? "bg-transparent"
    : "bg-[rgba(251,246,237,0.86)] backdrop-blur-[2px] dark:bg-[rgba(33,31,31,0.78)] dark:backdrop-blur-[2px]";

  const chrome = isTerminal
    ? "overflow-hidden rounded-lg border border-white/10 bg-[#0a0a0a] shadow-[0_10px_28px_rgba(0,0,0,0.52)]"
    : "relative overflow-hidden rounded-[min(16px,4vw)] border-2 border-current/65 bg-card-texture bg-cover bg-center shadow-lg [@media(min-width:768px)_and_(max-width:1279px)]:shadow-none";

  const glassShellPad = shellPadClassName ?? "p-[min(30px,6vw)]";

  const windowBodyPad =
    bodyPadClassName ??
    (isTerminal ? "" : "px-4 pb-3 pt-1.5 md:px-5 md:pb-4 md:pt-2");

  const headerRowPad = isTerminal ? "gap-2 px-2.5 py-1" : "gap-2.5 px-3 py-1.5";

  const titleClass =
    (isTerminal
      ? "font-mono text-[9px] font-medium uppercase tracking-[0.11em] text-emerald-500/95 sm:text-[10px] dark:text-emerald-400/95"
      : WINDOW_HEADER_TITLE_CLASS) + ` ${titleClassName}`;

  const titleLinkClass = `${titleClass} cursor-pointer underline-offset-[3px] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-1 dark:focus-visible:ring-offset-background-dark rounded-sm`;

  const titleNode =
    titleHref != null && titleHref !== "" ? (
      <Link
        href={titleHref}
        prefetch={false}
        className={titleLinkClass}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Site summary in plain text, for people and for tools (llms.txt)"
      >
        {title}
      </Link>
    ) : (
      <span className={titleClass}>{title}</span>
    );

  const headerRow = (
    <div
      className={`flex min-h-0 items-center ${headerRowPad} ${windowHeaderSurface}`}
    >
      {showShapeButtons ? <WindowShapeButtons /> : null}
      {titleNode}
    </div>
  );

  return (
    <motion.div
      drag={dragEnabled}
      dragConstraints={dragEnabled ? constrainRef : false}
      dragMomentum={false}
      dragElastic={0.06}
      whileDrag={dragEnabled ? { scale: 1.01 } : undefined}
      className={`${stackZClass} ${panelMaxClassName} ${dragEnabled ? "cursor-grab select-none active:cursor-grabbing" : "select-text"} ${className}`}
    >
      {!isTerminal ? (
        <div
          className={`pointer-events-none absolute -inset-x-4 -inset-y-3 -z-[1] rounded-[min(22px,5vw)] blur-2xl [@media(min-width:768px)_and_(max-width:1279px)]:hidden ${backdropAccentClassName ?? "bg-gradient-to-br from-brand-yellow/18 via-brand-pink/14 to-brand-teal/20"}`}
          aria-hidden
        />
      ) : null}
      <div
        className={
          isTerminal
            ? chrome
            : `rounded-[min(16px,4vw)] ${glassShellPad} text-text-light transition-colors duration-700 dark:text-text-dark [@media(min-width:768px)_and_(max-width:1279px)]:flex [@media(min-width:768px)_and_(max-width:1279px)]:min-h-0 [@media(min-width:768px)_and_(max-width:1279px)]:flex-1 [@media(min-width:768px)_and_(max-width:1279px)]:flex-col ${shellToneClassName ?? "bg-[#f6bbcd]/82 dark:bg-[#47353f]/75"}`
        }
      >
        <div
          className={
            isTerminal
              ? ""
              : `${chrome} [@media(min-width:768px)_and_(max-width:1279px)]:flex [@media(min-width:768px)_and_(max-width:1279px)]:min-h-0 [@media(min-width:768px)_and_(max-width:1279px)]:flex-1 [@media(min-width:768px)_and_(max-width:1279px)]:flex-col`
          }
        >
          {headerRow}
          <div
            className={`${isTerminal ? "rounded-b-lg" : "rounded-b-xl"} ${windowBodyPad} ${windowBodySurface} ${!isTerminal ? "[@media(min-width:768px)_and_(max-width:1279px)]:flex-1" : ""}`}
          >
            {children}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function HeroDraggableWindows() {
  const boundsRef = useRef<HTMLDivElement>(null);
  const { isMotionEnabled } = useMotion();
  const isXlUp = useIsXlUp();
  const dragEnabled = isMotionEnabled && isXlUp;
  const { scrollY } = useScroll();

  const fadeOpacity = useTransform(scrollY, [0, 100, 280], [1, 1, 0.38]);
  const fadeScale = useTransform(scrollY, [0, 120, 320], [1, 1, 0.92]);
  const fadeY = useTransform(scrollY, [0, 160, 360], [0, 0, -28]);

  return (
    <div className="min-w-0 overflow-x-clip">
      <motion.div
        className="pointer-events-none relative inset-x-0 top-0 z-[12] flex justify-center bg-transparent pt-[5.25rem] pb-[max(2rem,env(safe-area-inset-bottom))] md:pt-28 md:pb-[max(3.5rem,env(safe-area-inset-bottom))] [@media(min-width:768px)_and_(max-width:1279px)]:pt-24 [@media(min-width:768px)_and_(max-width:1279px)]:pb-[max(2.25rem,env(safe-area-inset-bottom))] xl:fixed xl:pb-[max(2rem,env(safe-area-inset-bottom))]"
        style={
          isMotionEnabled && isXlUp
            ? { opacity: fadeOpacity, scale: fadeScale, y: fadeY }
            : undefined
        }
      >
        <div
          ref={boundsRef}
          className="pointer-events-auto relative mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-0 overflow-x-clip overflow-y-visible px-3 pb-1 sm:px-4 [@media(min-width:768px)_and_(max-width:1279px)]:container [@media(min-width:768px)_and_(max-width:1279px)]:px-4 [@media(min-width:768px)_and_(max-width:1279px)]:grid [@media(min-width:768px)_and_(max-width:1279px)]:grid-cols-2 [@media(min-width:768px)_and_(max-width:1279px)]:content-center [@media(min-width:768px)_and_(max-width:1279px)]:items-stretch [@media(min-width:768px)_and_(max-width:1279px)]:gap-6 [@media(min-width:768px)_and_(max-width:1279px)]:min-h-[min(52svh,28rem)] [@media(min-width:768px)_and_(max-width:1279px)]:pb-6 [@media(min-width:1024px)_and_(max-width:1279px)]:gap-8 xl:max-w-7xl xl:flex xl:flex-col xl:min-h-[min(58vh,560px)] xl:gap-0 xl:px-6 xl:pb-0"
        >
          <div className="relative z-[10] mb-6 shrink-0 sm:mb-8 [@media(min-width:768px)_and_(max-width:1279px)]:mb-0 [@media(min-width:768px)_and_(max-width:1279px)]:min-h-0 [@media(min-width:768px)_and_(max-width:1279px)]:min-w-0 [@media(min-width:768px)_and_(max-width:1279px)]:self-stretch xl:absolute xl:inset-x-0 xl:top-8 xl:mb-0">
            <div className="container mx-auto px-4 md:px-6 [@media(min-width:768px)_and_(max-width:1279px)]:mx-0 [@media(min-width:768px)_and_(max-width:1279px)]:max-w-none [@media(min-width:768px)_and_(max-width:1279px)]:w-full [@media(min-width:768px)_and_(max-width:1279px)]:px-0 xl:mx-auto xl:px-6">
              <div className="w-full min-w-0 max-w-full md:max-w-[50rem] [@media(min-width:768px)_and_(max-width:1279px)]:mx-0 [@media(min-width:768px)_and_(max-width:1279px)]:max-w-none [@media(min-width:768px)_and_(max-width:1279px)]:px-0">
                <h1 className="pb-2 pt-0.5 text-[clamp(2.65rem,7.8vw,6rem)] font-semibold leading-[0.93] tracking-[-0.02em] text-text-light [text-shadow:0_1px_1px_rgba(255,255,255,0.9)] dark:text-text-dark dark:[text-shadow:0_1px_2px_rgba(0,0,0,0.75)] [@media(min-width:768px)_and_(max-width:1279px)]:text-[clamp(2.35rem,4.8vw,3.65rem)] [@media(min-width:768px)_and_(max-width:1279px)]:[text-shadow:none] [@media(min-width:768px)_and_(max-width:1279px)]:dark:[text-shadow:none] md:pb-3 md:pt-1">
                  Thoughtful <HeadlineTypewriter enabled={isMotionEnabled} />
                  <br className="sm:hidden" />{" "}
                  <span>built for work that matters.</span>
                </h1>
              </div>
            </div>
          </div>

          <DraggableWindow
            title="TOWNSHIP — ABOUT.TXT"
            titleHref="/llms.txt"
            stackZClass="z-[11]"
            constrainRef={boundsRef}
            dragEnabled={dragEnabled}
            showShapeButtons={isXlUp}
            panelMaxClassName="w-full max-w-full sm:max-w-xl [@media(min-width:768px)_and_(max-width:1279px)]:mx-0 [@media(min-width:768px)_and_(max-width:1279px)]:max-w-full xl:max-w-[min(46vw,34rem)]"
            shellToneClassName="bg-[#f2d6bf]/84 dark:bg-[#473f37]/78"
            backdropAccentClassName="bg-gradient-to-br from-brand-teal/20 via-brand-yellow/14 to-brand-pink/12"
            shellPadClassName="p-2.5 sm:p-3 [@media(min-width:768px)_and_(max-width:1279px)]:p-2.5 xl:p-[min(26px,5vw)] min-[1536px]:p-[min(30px,6vw)]"
            bodyPadClassName="px-3.5 pb-4 pt-1.5 sm:px-4 sm:pb-4 sm:pt-2 [@media(min-width:768px)_and_(max-width:1279px)]:px-3 [@media(min-width:768px)_and_(max-width:1279px)]:pb-3.5 [@media(min-width:768px)_and_(max-width:1279px)]:pt-1.5 xl:px-6 xl:pb-6 xl:pt-3"
            titleClassName="min-w-0 shrink whitespace-normal [@media(min-width:1280px)]:whitespace-nowrap"
            className="relative z-[11] mx-auto mt-4 w-full max-w-full shrink-0 min-w-0 sm:mt-6 [@media(min-width:768px)_and_(max-width:1279px)]:mx-0 [@media(min-width:768px)_and_(max-width:1279px)]:mt-0 [@media(min-width:768px)_and_(max-width:1279px)]:flex [@media(min-width:768px)_and_(max-width:1279px)]:min-h-0 [@media(min-width:768px)_and_(max-width:1279px)]:h-full [@media(min-width:768px)_and_(max-width:1279px)]:flex-col xl:absolute xl:inset-x-0 xl:mx-auto xl:mt-0 xl:left-auto xl:right-0 xl:top-8"
          >
            <p className="text-lg font-light leading-relaxed text-text-muted-light [text-shadow:0_1px_1px_rgba(255,255,255,0.92)] dark:text-text-muted-dark dark:[text-shadow:0_1px_2px_rgba(0,0,0,0.65)] sm:text-xl [@media(min-width:768px)_and_(max-width:1279px)]:text-base [@media(min-width:768px)_and_(max-width:1279px)]:leading-relaxed [@media(min-width:768px)_and_(max-width:1279px)]:[text-shadow:none] [@media(min-width:768px)_and_(max-width:1279px)]:dark:[text-shadow:none] xl:text-[1.5rem] xl:leading-[1.45] min-[1536px]:text-[1.6875rem]">
              {SITE_TAGLINE}
            </p>
            <div className="mt-4 md:mt-5">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-full bg-text-light px-4 py-2 text-sm font-semibold text-tan shadow-[0_4px_14px_rgba(0,0,0,0.14)] transition hover:scale-[1.02] hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 [@media(min-width:768px)_and_(max-width:1279px)]:shadow-none dark:bg-brand-teal dark:text-background-dark dark:shadow-[0_4px_14px_rgba(0,0,0,0.35)] [@media(min-width:768px)_and_(max-width:1279px)]:dark:shadow-none md:px-5 md:py-2.5"
              >
                Contact Us
              </Link>
            </div>
          </DraggableWindow>
        </div>
      </motion.div>

      {/* Flow spacer: first screen height; page content below scrolls over the fixed windows */}
      <div
        className="hidden min-h-[min(69vh,610px)] w-full shrink-0 xl:block"
        aria-hidden
      />
    </div>
  );
}
